// Per-user chart-type preferences.
//
// Two layers, both fetched at mount and cached on the module:
//   * value_type: user-chosen default for every feature of that value type.
//   * feature: per-feature override.
//
// Resolution: feature override → value-type default → system default
// (declared in the chart registry).
//
// We deliberately keep this as a tiny module-local cache + a single shared
// fetch promise rather than a React context: only FeatureChartCard
// consumes it, prefs are small (one row per feature touched), and a
// context provider would force callers to wrap their tree.

import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { ChartId, ValueType } from './types';

interface ChartPrefsResponse {
  value_type: Record<string, string>;
  feature: Record<string, string>;
}

let cache: ChartPrefsResponse | null = null;
let inflight: Promise<ChartPrefsResponse> | null = null;
const subscribers = new Set<() => void>();

async function loadOnce(): Promise<ChartPrefsResponse> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const resp = await api.get<ChartPrefsResponse>('/api/users/me/chart_prefs');
      cache = resp.data;
      return cache;
    } catch (err) {
      cache = { value_type: {}, feature: {} };
      return cache;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

function notify() {
  subscribers.forEach((fn) => fn());
}

export interface ChartPrefsApi {
  loaded: boolean;
  // Resolve the chart_id for a (value_type, feature_id) pair against the
  // cached prefs, taking only a pref that is in the value type's `cycle`.
  // A stale pref — a per-feature pick from before the feature's value type
  // changed, e.g. an enum's `pie` on a feature converted to yes/no — is
  // skipped, and `cycle[0]` (the system default) applies when none fits.
  resolve(valueType: ValueType, featureId: number, cycle: readonly ChartId[]): ChartId;
  // Per-feature pick. Writes only the feature override.
  setFeatureChart(featureId: number, chartId: ChartId): Promise<void>;
  // Clear a per-feature override so the value-type default takes over.
  clearFeatureChart(featureId: number): Promise<void>;
  // "Make this the default for all features of this value type." Writes
  // the value-type pref. Does NOT touch any feature overrides.
  setValueTypeChart(valueType: ValueType, chartId: ChartId): Promise<void>;
  // Whether the user has set an explicit value-type default (vs the
  // hardcoded system default). Used by the switcher to disable the
  // "make default" action when the chart is already the default.
  getValueTypePref(valueType: ValueType): ChartId | undefined;
  getFeaturePref(featureId: number): ChartId | undefined;
}

export function useChartPrefs(): ChartPrefsApi {
  const [, setTick] = useState(0);

  useEffect(() => {
    let mounted = true;
    const sub = () => {
      if (mounted) setTick((n) => n + 1);
    };
    subscribers.add(sub);
    if (!cache) loadOnce().then(() => mounted && setTick((n) => n + 1));
    return () => {
      mounted = false;
      subscribers.delete(sub);
    };
  }, []);

  return {
    loaded: cache !== null,
    resolve(valueType, featureId, cycle) {
      const inCycle = (id: string | undefined): ChartId | undefined =>
        id && (cycle as readonly string[]).includes(id) ? (id as ChartId) : undefined;
      return inCycle(cache?.feature[String(featureId)])
        ?? inCycle(cache?.value_type[valueType])
        ?? cycle[0];
    },
    getValueTypePref(valueType) {
      return cache?.value_type[valueType] as ChartId | undefined;
    },
    getFeaturePref(featureId) {
      return cache?.feature[String(featureId)] as ChartId | undefined;
    },
    async setFeatureChart(featureId, chartId) {
      const resp = await api.put<ChartPrefsResponse>(
        `/api/users/me/chart_prefs/feature/${featureId}`,
        { chart_id: chartId },
      );
      cache = resp.data;
      notify();
    },
    async clearFeatureChart(featureId) {
      const resp = await api.delete<ChartPrefsResponse>(
        `/api/users/me/chart_prefs/feature/${featureId}`,
      );
      cache = resp.data;
      notify();
    },
    async setValueTypeChart(valueType, chartId) {
      const resp = await api.put<ChartPrefsResponse>(
        `/api/users/me/chart_prefs/value_type/${valueType}`,
        { chart_id: chartId },
      );
      cache = resp.data;
      notify();
    },
  };
}
